/**
 * Selettore dell'indirizzo Reply-To dell'entità mittente (§11.1).
 * - Mostra solo gli indirizzi VERIFICATI.
 * - Default = indirizzo con is_default.
 * - Permette di aggiungere un nuovo indirizzo (dialog OTP) se l'utente ha NEWSLETTER W.
 *
 * Riferimento: TDD Sprint 18 §8.7.
 */
import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Grid, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { CustomAutocomplete } from '../../../../utils/StyledInputs';
import { DefaultOutlinedButton } from '../../../../utils/Buttons';
import { HttpErrorHandler } from '../../../../utils/ErrorHandler';
import { usePopup } from '../../../../context/PopupContext';
import { replyToApi } from '../../../../services/newsletter/replyToApi';
import { ReplyToOtpDialog } from './ReplyToOtpDialog';

/**
 * @param {{
 *   entityId: string|null,
 *   value: string|null,                 // reply_to_email_id selezionato
 *   onChange: (id: string|null) => void,
 *   canWrite?: boolean,
 *   disabled?: boolean,
 * }} props
 */
export function ReplyToSelector({ entityId, value, onChange, canWrite = false, disabled = false, error = false }) {
  const { showPopup, setPopUpText } = usePopup();

  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);

  const fetchAddresses = useCallback(
    (selectEmail) => {
      if (!entityId) {
        setAddresses([]);
        return;
      }
      setLoading(true);
      replyToApi
        .list(entityId)
        .then((res) => {
          // La risposta è un array di indirizzi; "verificato" = verified_at valorizzato
          // (oppure verified === true come fallback).
          const list = Array.isArray(res.data) ? res.data : res.data?.data ?? [];
          const verified = list.filter((a) => a.verified === true || !!a.verified_at);
          setAddresses(verified);

          // Selezione: indirizzo appena verificato → altrimenti mantieni il valore corrente
          // se ancora valido per questa entità → altrimenti default → altrimenti nessuno.
          if (selectEmail) {
            const justAdded = verified.find((a) => a.email === selectEmail);
            if (justAdded) onChange(justAdded.id);
            return;
          }
          const stillValid = value && verified.some((a) => a.id === value);
          if (!stillValid) {
            const def = verified.find((a) => a.is_default);
            onChange(def ? def.id : null);
          }
        })
        .catch((err) => {
          HttpErrorHandler(err, setPopUpText);
          showPopup(false);
        })
        .finally(() => setLoading(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entityId]
  );

  useEffect(() => {
    fetchAddresses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  const selected = addresses.find((a) => a.id === value) ?? null;

  const optionLabel = (a) => (a.label ? `${a.label} · ${a.email}` : a.email);

  return (
    <Box>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={canWrite ? 8 : 12}>
          <CustomAutocomplete
            id="reply-to-email"
            disabled={disabled || !entityId || loading}
            options={addresses}
            value={selected}
            loading={loading}
            isOptionEqualToValue={(option, v) => option.id === v.id}
            getOptionLabel={optionLabel}
            renderOption={(props, option) => (
              <Box component="li" {...props} key={option.id}>
                {optionLabel(option)}
                {option.is_default ? ' (predefinito)' : ''}
              </Box>
            )}
            onChange={(_e, v) => onChange(v ? v.id : null)}
            renderInput={(params) => (
              <TextField {...params} label="Indirizzo Reply-To" variant="outlined" error={error} />
            )}
          />
        </Grid>
        {canWrite && (
          <Grid item xs={12} md={4}>
            <DefaultOutlinedButton
              startIcon={<AddIcon />}
              disabled={disabled || !entityId}
              customStyles={{ width: '100%' }}
              onClick={() => setOpenDialog(true)}
            >
              Aggiungi indirizzo
            </DefaultOutlinedButton>
          </Grid>
        )}
      </Grid>

      {!loading && entityId && addresses.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Nessun indirizzo verificato per questa entità.
          {canWrite ? ' Aggiungine uno con la verifica OTP.' : ''}
        </Typography>
      )}

      <Stack>
        <ReplyToOtpDialog
          open={openDialog}
          entityId={entityId}
          onClose={() => setOpenDialog(false)}
          onConfirmed={(email) => fetchAddresses(email)}
        />
      </Stack>
    </Box>
  );
}

ReplyToSelector.propTypes = {
  entityId: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  canWrite: PropTypes.bool,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
};

export default ReplyToSelector;